import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import axios from 'axios';
import { showSuccess, showError } from '../../utils/toast';
import { API_URL } from '../../config';
import '../../App.css';

const IFTA_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
  'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
  'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
];

const LogTripModal = ({ isOpen, onClose, onSuccess, initialTrip }) => {
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    tripDate: today,
    startLocation: '',
    endLocation: '',
    totalMiles: '',
    vehicleId: '',
    notes: '',
  });

  const [stateMileage, setStateMileage] = useState([{ state: '', miles: '' }]);
  const [fuelPurchases, setFuelPurchases] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (initialTrip) {
      // Populate form with existing trip data
      setFormData({
        tripDate: initialTrip.tripDate.split('T')[0],
        startLocation: initialTrip.startLocation || '',
        endLocation: initialTrip.endLocation || '',
        totalMiles: initialTrip.totalMiles?.toString() || '',
        vehicleId: initialTrip.vehicleId || '',
        notes: initialTrip.notes || '',
      });

      // Set state mileage
      if (initialTrip.mileageByState) {
        const states = Object.entries(initialTrip.mileageByState).map(([state, miles]) => ({
          state,
          miles: miles.toString(),
        }));
        setStateMileage(states.length > 0 ? states : [{ state: '', miles: '' }]);
      }

      // Set fuel purchases
      if (initialTrip.fuelPurchases && Array.isArray(initialTrip.fuelPurchases)) {
        setFuelPurchases(
          initialTrip.fuelPurchases.map((p) => ({
            state: p.state || '',
            gallons: p.gallons?.toString() || '',
            price: p.price?.toString() || '',
            location: p.location || '',
          }))
        );
      }
    } else {
      // Reset form for new trip
      setFormData({
        tripDate: today,
        startLocation: '',
        endLocation: '',
        totalMiles: '',
        vehicleId: '',
        notes: '',
      });
      setStateMileage([{ state: '', miles: '' }]);
      setFuelPurchases([]);
    }
  }, [initialTrip, isOpen, today]);

  useEffect(() => {
    // Calculate total from state mileage
    const total = stateMileage.reduce((sum, item) => {
      const miles = parseFloat(item.miles) || 0;
      return sum + miles;
    }, 0);
    setCalculatedTotal(total);
  }, [stateMileage]);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleStateMileageChange = (index, field, value) => {
    const updated = [...stateMileage];
    updated[index] = { ...updated[index], [field]: value };
    setStateMileage(updated);
  };

  const addStateMileage = () => {
    setStateMileage([...stateMileage, { state: '', miles: '' }]);
  };

  const removeStateMileage = (index) => {
    if (stateMileage.length > 1) {
      setStateMileage(stateMileage.filter((_, i) => i !== index));
    }
  };

  const addFuelPurchase = () => {
    setFuelPurchases([
      ...fuelPurchases,
      { state: '', gallons: '', price: '', location: '' },
    ]);
  };

  const handleFuelPurchaseChange = (index, field, value) => {
    const updated = [...fuelPurchases];
    updated[index] = { ...updated[index], [field]: value };
    setFuelPurchases(updated);
  };

  const removeFuelPurchase = (index) => {
    setFuelPurchases(fuelPurchases.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tripDate) newErrors.tripDate = 'Trip date is required';
    if (!formData.startLocation) newErrors.startLocation = 'Start location is required';
    if (!formData.endLocation) newErrors.endLocation = 'End location is required';
    if (!formData.totalMiles || parseFloat(formData.totalMiles) <= 0) {
      newErrors.totalMiles = 'Total miles must be greater than 0';
    }

    // Validate state mileage
    const validStates = stateMileage.filter((item) => item.state && item.miles);
    if (validStates.length === 0) {
      newErrors.stateMileage = 'At least one state with mileage is required';
    }

    // Check if total matches
    const totalMiles = parseFloat(formData.totalMiles) || 0;
    if (Math.abs(calculatedTotal - totalMiles) > 0.01) {
      newErrors.totalMiles = `Total miles (${totalMiles}) must match sum of state miles (${calculatedTotal.toFixed(2)})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      // Build mileageByState object
      const mileageByState = {};
      stateMileage.forEach((item) => {
        if (item.state && item.miles) {
          mileageByState[item.state] = parseFloat(item.miles);
        }
      });

      // Build fuelPurchases array
      const validFuelPurchases = fuelPurchases
        .filter((p) => p.state && p.gallons && p.price)
        .map((p) => ({
          state: p.state,
          gallons: parseFloat(p.gallons),
          price: parseFloat(p.price),
          location: p.location || '',
        }));

      const tripData = {
        tripDate: formData.tripDate,
        startLocation: formData.startLocation,
        endLocation: formData.endLocation,
        totalMiles: parseFloat(formData.totalMiles),
        mileageByState,
        fuelPurchases: validFuelPurchases.length > 0 ? validFuelPurchases : undefined,
        notes: formData.notes || undefined,
        vehicleId: formData.vehicleId || undefined,
      };

      if (initialTrip) {
        // Update existing trip
        await axios.put(
          `${API_URL}/api/ifta/trips/${initialTrip.id}`,
          tripData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        showSuccess('Trip updated successfully!');
      } else {
        // Create new trip
        await axios.post(`${API_URL}/api/ifta/trips`, tripData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        showSuccess('Trip logged successfully!');
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error logging trip:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to log trip. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedDown: (eventData) => {
      // Only close on downward swipe from top of modal
      if (eventData.first) {
        onClose();
      }
    },
    trackMouse: true,
    delta: 50, // Minimum swipe distance
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        {...swipeHandlers}
      >
        <div className="modal-header">
          <h2>{initialTrip ? 'Edit IFTA Trip' : 'Log IFTA Trip'}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              Trip Date <span className="required">*</span>
            </label>
            <input
              type="date"
              name="tripDate"
              value={formData.tripDate}
              onChange={handleInputChange}
              className={errors.tripDate ? 'error' : ''}
            />
            {errors.tripDate && <span className="error-text">{errors.tripDate}</span>}
          </div>

          <div className="form-group">
            <label>
              Start Location <span className="required">*</span>
            </label>
            <input
              type="text"
              name="startLocation"
              value={formData.startLocation}
              onChange={handleInputChange}
              placeholder="Kansas City, MO"
              className={errors.startLocation ? 'error' : ''}
            />
            {errors.startLocation && (
              <span className="error-text">{errors.startLocation}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              End Location <span className="required">*</span>
            </label>
            <input
              type="text"
              name="endLocation"
              value={formData.endLocation}
              onChange={handleInputChange}
              placeholder="Denver, CO"
              className={errors.endLocation ? 'error' : ''}
            />
            {errors.endLocation && (
              <span className="error-text">{errors.endLocation}</span>
            )}
          </div>

          <div className="form-group">
            <label>
              Total Miles <span className="required">*</span>
            </label>
            <input
              type="number"
              name="totalMiles"
              value={formData.totalMiles}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={errors.totalMiles ? 'error' : ''}
            />
            {errors.totalMiles && (
              <span className="error-text">{errors.totalMiles}</span>
            )}
            {calculatedTotal > 0 && (
              <span className="helper-text">
                Calculated from states: {calculatedTotal.toFixed(2)} miles
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Vehicle (Optional)</label>
            <select
              name="vehicleId"
              value={formData.vehicleId}
              onChange={handleInputChange}
            >
              <option value="">Select a vehicle</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>States & Mileage</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addStateMileage}
              >
                + Add State
              </button>
            </div>
            {errors.stateMileage && (
              <span className="error-text">{errors.stateMileage}</span>
            )}
            <div className="state-mileage-inputs">
              {stateMileage.map((item, index) => (
                <div key={index} className="state-input-row">
                  <select
                    value={item.state}
                    onChange={(e) =>
                      handleStateMileageChange(index, 'state', e.target.value)
                    }
                  >
                    <option value="">Select State</option>
                    {IFTA_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Miles"
                    value={item.miles}
                    onChange={(e) =>
                      handleStateMileageChange(index, 'miles', e.target.value)
                    }
                    step="0.01"
                    min="0"
                  />
                  {stateMileage.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeStateMileage(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h3>Fuel Purchases (Optional)</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addFuelPurchase}
              >
                + Add Purchase
              </button>
            </div>
            {fuelPurchases.map((purchase, index) => (
              <div key={index} className="fuel-purchase-row">
                <select
                  value={purchase.state}
                  onChange={(e) =>
                    handleFuelPurchaseChange(index, 'state', e.target.value)
                  }
                >
                  <option value="">State</option>
                  {IFTA_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Gallons"
                  value={purchase.gallons}
                  onChange={(e) =>
                    handleFuelPurchaseChange(index, 'gallons', e.target.value)
                  }
                  step="0.01"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Price/Gallon"
                  value={purchase.price}
                  onChange={(e) =>
                    handleFuelPurchaseChange(index, 'price', e.target.value)
                  }
                  step="0.01"
                  min="0"
                />
                <input
                  type="text"
                  placeholder="Location"
                  value={purchase.location}
                  onChange={(e) =>
                    handleFuelPurchaseChange(index, 'location', e.target.value)
                  }
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => removeFuelPurchase(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>Notes (Optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              placeholder="Additional trip notes..."
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Logging...' : 'Log Trip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogTripModal;

